import React, { useState, useRef, useEffect } from 'react';

/* Additional imports for icons and utilities as used in the component */
import { Send, User, Loader2, CheckCircle, ShoppingBag, Plus } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { cn } from '../../../utils/cn';
import { sanitizeWebsiteUrl } from './utils';

const AIAssessmentChat = ({ onComplete, onClose, context = 'category-selection', existingData = null, persistedMessages, persistedUserResponses, persistedConversationState, onStateChange, onAddSingleOption }) => {
  const getInitialMessages = () => {
    // Treat empty arrays the same as null — always show the context-aware greeting
    if (persistedMessages && persistedMessages?.length > 0) return persistedMessages;
    const purchaseType = existingData?.basics?.purchaseType || 'item';
    const projectName = existingData?.basics?.projectName || '';
    const specs = existingData?.specs || {};
    const budget = existingData?.basics?.budgetRange || '';

    // Build a concise summary of the user's known needs from specs
    const specEntries = Object.entries(specs)?.filter(([key, val]) => key !== 'weightings' && val && val !== '' && val !== false)?.slice(0, 6)?.map(([key, val]) => `• ${key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}: ${val}`)?.join('\n');

    const needsSummary = specEntries
      ? `\n\nBased on your needs so far:\n${specEntries}`
      : '';

    const budgetNote = budget ? ` with a budget of ${budget}` : '';

    return [
      {
        id: 1,
        role: 'assistant',
        content: context === 'options-finder' ? `Hi! I'm here to help you find the best purchase options for your ${purchaseType}${budgetNote}.${needsSummary}\n\nI'll search for specific options that match your requirements and recommend the best places to buy. You can ask me to find options, refine by price or features, or continue from where we left off in your needs assessment.\n\nWhat would you like me to find for you?`
          : context === 'needs' ? `Hi! I'm your Needs Assistant. Tell me about what you're looking for — describe it in your own words. For example, tell me the type, style, size, material, budget, or any must-have features.\n\nOnce I understand your needs, I'll help populate your product details automatically.`
          : context === 'product-details' ? `Hi! I'm your AI Product Assistant. I'll read through your project details and help you fill in the right specifications for the Needs page.\n\nTo get started — can you tell me a bit about what you're looking for? For example, how you plan to use it, any must-have features, or your budget range?`
          : '',
        showRecommendationButton: false
      }
    ];
  };

  const [messages, setMessages] = useState(getInitialMessages);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState(persistedConversationState || 'gathering');
  const [userResponses, setUserResponses] = useState(persistedUserResponses || []);
  const [addedOptions, setAddedOptions] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync state changes back to parent for persistence
  useEffect(() => {
    onStateChange?.(messages, userResponses, conversationState);
  }, [messages, userResponses, conversationState]);

  // Add intelligent response detection function
  const generateIntelligentResponse = (userMessage) => {
    const message = userMessage?.toLowerCase();

    // Pattern for professional laptop users with high workload
    const professionalPatterns = [
      'work laptop',
      'email accounts',
      'video calls',
      'llms',
      'large files',
      'streaming',
      'advertising videos',
      'handles all work functions',
      'flawlessly'
    ];

    const matchingPatterns = professionalPatterns?.filter(pattern =>
      message?.includes(pattern)
    );

    // If user describes a high-performance work laptop scenario
    if (matchingPatterns?.length >= 4 && message?.includes('mac')) {
      return {
        isIntelligent: true,
        response: "What you're looking for is a Mac that is going to feel fast for years to come, able to handle your workload, streaming requirements, and with plenty of storage for your ad files. I've got a good idea of what you need. Mind if I populate the needs on the product detail page for your review?",
        suggestedSpecs: {
          processor: 'Apple M3 Pro or M3 Max (for video processing)',
          ram: '32GB unified memory (for multiple email accounts and LLMs)',
          storage: '1TB+ SSD (for large advertising video files)',
          display: '14" or 16" Liquid Retina XDR (for content review)',
          connectivity: 'Thunderbolt 4 ports, excellent WiFi 6E',
          battery: 'All-day battery life for video calls',
          performance_tier: 'Professional/High-Performance',
          use_case: 'Professional content creation and heavy multitasking'
        }
      };
    }

    // Gaming laptop pattern
    if (message?.includes('gaming') && (message?.includes('performance') || message?.includes('graphics'))) {
      return {
        isIntelligent: true,
        response: "Sounds like you need a powerful gaming machine that can handle the latest titles at high settings. Based on what you've described, I can recommend specifications that will give you excellent gaming performance. Would you like me to populate some gaming-focused requirements for your review?",
        suggestedSpecs: {
          processor: 'Intel i7/i9 or AMD Ryzen 7/9',
          ram: '16GB-32GB DDR5',
          graphics: 'RTX 4060/4070 or better',
          storage: '1TB NVMe SSD',
          display: '144Hz+ gaming display',
          cooling: 'Advanced thermal management'
        }
      };
    }

    // Student/budget laptop pattern
    if ((message?.includes('student') || message?.includes('school') || message?.includes('budget')) &&
        (message?.includes('basic') || message?.includes('simple') || message?.includes('affordable'))) {
      return {
        isIntelligent: true,
        response: "I understand you're looking for a reliable, budget-friendly laptop for school or basic tasks. I can suggest specifications that will give you good performance without breaking the bank. Should I populate some student-friendly options for your consideration?",
        suggestedSpecs: {
          processor: 'Intel i5 or AMD Ryzen 5',
          ram: '8GB-16GB',
          storage: '256GB-512GB SSD',
          battery: 'Long battery life for all-day use',
          portability: 'Lightweight and portable',
          budget_range: 'Budget-conscious options'
        }
      };
    }
    return { isIntelligent: false };
  };

  // Detect if a user message is explicitly requesting a product/options search
  const isSearchIntent = (message) => {
    const lower = message?.toLowerCase() || '';
    const searchKeywords = [
      'find me', 'search for', 'look for', 'show me options', 'find options',
      'where can i buy', 'where to buy', 'what should i buy',
      'give me options', 'look up', 'find the best', 'search the web',
      'find products', 'find vendors', 'find suppliers'
    ];
    return searchKeywords?.some(kw => lower?.includes(kw));
  };

  // Robust JSON extractor — handles <think> reasoning prefix + code fences
  const extractJSON = (text) => {
    if (!text) return null;
    const jsonFenceMatch = text?.match(/```json\s*([\s\S]*?)\s*```/i);
    if (jsonFenceMatch) { try { return JSON.parse(jsonFenceMatch?.[1]?.trim()); } catch (_) {} }
    const anyFenceMatch = text?.match(/```\s*([\s\S]*?)\s*```/);
    if (anyFenceMatch) { try { return JSON.parse(anyFenceMatch?.[1]?.trim()); } catch (_) {} }
    const jsonObjectMatch = text?.match(/\{[\s\S]*\}/);
    if (jsonObjectMatch) { try { return JSON.parse(jsonObjectMatch?.[0]); } catch (_) {} }
    return null;
  };

  const analyzeUserNeeds = async (responses, forceSearch = false, messageText = '') => {
    try {
      const conversationSummary = responses?.join(' | ');
      const purchaseType = existingData?.basics?.purchaseType || 'general';

      // Exact field schemas per purchase type for product-details context
      const productSpecSchemas = {
        'computers-laptops': `{
  "priceMax": "budget or max price (e.g. $2,000)",
  "processor": "CPU model or spec (e.g. Apple M3 Pro, Intel i7-13700H)",
  "ram": "memory amount (e.g. 16GB, 32GB)",
  "storage": "storage size and type (e.g. 512GB SSD, 1TB NVMe)",
  "graphics": "GPU model or spec (e.g. NVIDIA RTX 4060, Apple GPU 18-core)",
  "screenSize": "display size in inches (e.g. 15.6 inches, 14-inch)",
  "screenResolution": "display resolution (e.g. 1920x1080, 2560x1664 Retina)",
  "batteryLife": "battery duration (e.g. 10+ hours, all-day)",
  "ports": "connectivity ports (e.g. 2x USB-C Thunderbolt, HDMI, USB-A)",
  "weight": "device weight (e.g. 3.5 lbs, under 4 lbs)",
  "operatingSystem": "one of: windows | macos | linux | chrome",
  "warranty": "warranty terms (e.g. 1 year manufacturer, AppleCare+)"
}`,
        'tablets': `{
  "priceMax": "budget or max price (e.g. $800)",
  "storage": "one of: 64GB | 128GB | 256GB | 512GB | 1TB",
  "batteryLife": "battery duration (e.g. 10+ hours)",
  "screenSize": "display size in inches (e.g. 10.9 inches, 12.9 inches)",
  "cameraSpecs": "camera details (e.g. 12MP rear, 4K video)",
  "processor": "chip model (e.g. Apple M2, Snapdragon 8 Gen 2)",
  "carrierCompatibility": "one of: unlocked | verizon | att | tmobile | wifi-only",
  "waterproofRating": "one of: IP68 | IP67 | IP65 | not-rated",
  "warranty": "warranty terms (e.g. 1 year manufacturer)"
}`,
        'smartphones': `{
  "priceMax": "budget or max price (e.g. $1,000)",
  "storage": "one of: 128GB | 256GB | 512GB | 1TB",
  "screenSize": "display size in inches (e.g. 6.1 inches)",
  "batteryLife": "battery capacity or duration (e.g. 4000mAh, all-day)",
  "processor": "chip model (e.g. Apple A17 Pro, Snapdragon 8 Gen 3)",
  "carrierCompatibility": "one of: unlocked | verizon | att | tmobile",
  "waterproofRating": "one of: IP68 | IP67 | IP65 | not-rated",
  "warranty": "warranty terms (e.g. 1 year manufacturer)",
  "fiveGSupport": true,
  "wirelessCharging": true,
  "biometricAuth": true,
  "dualSim": false,
  "fastCharging": true,
  "expandableStorage": false
}`,
        'car-purchase': `{
  "vehicleType": "one of: sedan | suv | truck | coupe | hatchback | convertible",
  "priceMax": "budget or max price (e.g. $35,000)",
  "makeModelYear": "make, model, and year (e.g. 2024 Toyota Camry)",
  "totalCost": "total price including taxes and fees (e.g. $35,000)",
  "mileage": "mileage if used (e.g. 25,000 miles) or 'New'",
  "condition": "one of: new | used | certified",
  "fuelType": "one of: gasoline | hybrid | electric | diesel",
  "features": "additional features (e.g. sunroof, AWD)"
}`,
        'home-purchase': `{
  "purchasePrice": "target purchase price (e.g. $450,000)",
  "mortgageInterestRate": "expected interest rate (e.g. 6.5%)",
  "loanTerms": "one of: 15-year fixed | 20-year fixed | 30-year fixed | 5/1 ARM | 7/1 ARM",
  "downPaymentRequirements": "down payment amount or % (e.g. 20%, $90,000)",
  "propertyTaxes": "estimated annual property taxes (e.g. $4,500/year)",
  "insuranceCosts": "estimated insurance costs (e.g. $1,800/year)",
  "hoaCondoFees": "HOA or condo fees (e.g. $150/month or 'None')",
  "homeCondition": "one of: move-in-ready | needs-minor-repairs | fixer-upper | new-construction",
  "locationPreferences": "location priorities",
  "closingTimeline": "one of: 30 days | 45 days | 60 days | 90 days | flexible"
}`,
        'home-refinance': `{
  "currentBalance": "current loan balance (e.g. $250,000)",
  "currentRate": "current interest rate (e.g. 4.5%)",
  "homeValue": "estimated current home value (e.g. $400,000)",
  "loanTerm": "one of: 15-year | 20-year | 30-year",
  "lowerPayment": true,
  "cashOut": false
}`,
        'furniture': `{
  "category": "one of: sofa | bed | dining-table | desk | dresser | bookcase | other",
  "material": "one of: wood | metal | fabric | leather | glass | plastic | mixed",
  "style": "one of: modern | traditional | rustic | industrial | scandinavian",
  "color": "preferred colors or finishes",
  "size": "dimensions or size preferences",
  "warranty": "warranty terms",
  "leadTime": "delivery timeline (e.g. in-stock, 2-4 weeks)"
}`
      };

      const specSchema = productSpecSchemas?.[purchaseType] || `{}`;

      // ── Two-mode system prompt for options-finder ──────────────────────────
      // forceSearch = true  → JSON-only prompt (search triggered explicitly)
      // forceSearch = false → natural conversational prompt
      let systemPrompt;
      if (context === 'options-finder') {
        if (forceSearch) {
          systemPrompt = `You are an expert shopping advisor with real-time web search capabilities. The user is looking for a ${purchaseType} purchase. Based on their conversation and the following known needs/specs: ${JSON.stringify(existingData?.specs || {})}, search for and recommend 3-5 specific, current purchase options.\n        \n        Include:\n        - Specific product models with current pricing\n        - Best retailers (Amazon, Best Buy, manufacturer websites, specialty stores, etc.)\n        - Why each option fits their stated needs\n        - Price comparison across retailers\n        \n        Focus on generating 3-5 specific, actionable purchase options with realistic details including estimated price ranges and key benefits of each option.\n\nIMPORTANT URL RULES:\n- The "website" field MUST be the vendor root domain only (e.g. "livingspaces.com", "amazon.com", "bestbuy.com"). No display names, no parentheses.\n- The "product_url" field MUST be the DIRECT, SPECIFIC product page URL for that exact item (e.g. "https://www.livingspaces.com/products/specific-sofa-12345", "https://www.amazon.com/dp/B08XYZ123"). This must link directly to the product, NOT the homepage. If you cannot find the exact product URL, use the most specific category/search URL you can find.\n\nRespond with valid JSON only in this exact format: {"suggested_options": [{"name": string, "website": string, "product_url": string, "price_range": string, "benefits": string, "notes": string}], "confidence": number, "reasoning": string, "next_steps": [string]}`;
        } else {
          systemPrompt = `You are a friendly and knowledgeable shopping advisor helping a user find the best ${purchaseType} for their needs. Have a natural, helpful conversation to understand what they're looking for. Ask clarifying questions about their budget, preferences, use case, and any must-have features. Keep responses concise and conversational — 2-4 sentences max. Do NOT return JSON. When you have enough information, let the user know they can click "Find Options for Me" to get specific product recommendations.`;
        }
      } else if (context === 'product-details') {
        systemPrompt = `You are an expert product specification analyst. Based on user responses about their ${purchaseType} purchase, determine specific product requirements and fill in the product_specifications object using EXACTLY the field keys shown in the schema below. Do not invent new keys — use only the keys provided in the schema.\n\nRequired product_specifications schema for ${purchaseType}:\n${specSchema}\n\nFor boolean fields (true/false), return actual boolean values. For Select fields, return one of the listed options exactly as shown. Fill in every field with a reasonable value based on the user's responses.\n\nRespond with valid JSON only in this exact format: {"product_specifications": ${specSchema}, "recommended_features": [string], "budget_recommendation": string, "quality_tier": string, "timeline_suggestion": string, "confidence": number, "reasoning": string, "next_steps": [string]}`;
      } else {
        systemPrompt = `You are an expert purchase category analyst. Based on user responses, determine the most appropriate purchase category and subcategory from the Stackd platform.\n\nCategories available:\n- Tech & Electronics\n- Software & Digital Services\n- Home & Property\n- Home Purchases/Durable Goods\n- Vehicle & Transportation\n- Life Events & Family\n- Travel & Experiences\n- Financial & Professional Services\n- Health & Personal Care\n\nRespond with valid JSON only in this exact format: {"recommended_category": string, "recommended_subcategory": string, "confidence": number, "reasoning": string, "budget_estimate": string, "timeline_suggestion": string, "next_steps": [string]}`;
      }

      const userContent = context === 'options-finder'
        ? forceSearch
          ? `Find the best purchase options for a ${purchaseType} with the following requirements:\n${Object.entries(existingData?.specs || {})?.filter(([, v]) => v && v !== '' && v !== false)?.map(([k, v]) => `\u2022 ${k?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}: ${v}`)?.join('\n')}\nBudget: ${existingData?.basics?.budgetRange || 'not specified'}\nUser message: "${messageText || responses?.[responses?.length - 1] || ''}"${conversationSummary ? `\nAdditional context: ${conversationSummary}` : ''}`
          : `${messageText || responses?.[responses?.length - 1] || ''}`
        : context === 'product-details'
        ? `User is looking for a ${existingData?.basics?.purchaseType || 'product'}. They said: "${messageText || responses?.[responses?.length - 1] || ''}". Previous responses: ${responses?.slice(0, -1)?.join(', ')}`
        : `User said: "${messageText || responses?.[responses?.length - 1] || ''}". Previous responses: ${responses?.slice(0, -1)?.join(', ')}`;

      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-reasoning-pro',
        [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: userContent
          }
        ],
        { max_tokens: context === 'options-finder' ? 1500 : 800 }
      );

      const rawContent = result?.choices?.[0]?.message?.content || '';

      let parsed = extractJSON(rawContent);

      // options-finder search mode: surface parsed options with per-option Add buttons
      if (context === 'options-finder' && forceSearch) {
        if (parsed?.suggested_options?.length > 0) {
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: `Here are the best options I found for your ${purchaseType}. Click "Add to Stack" on any option to save it, or use "Add All & Close" to add everything at once.`,
            isAnalysis: true,
            analysisData: parsed
          };
          setMessages(prev => [...prev, assistantMessage]);
          setConversationState('complete');
        } else {
          const fallbackContent = rawContent?.trim() || "I wasn't able to find specific options. Could you share more details about your budget or preferences?";
          setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: fallbackContent }]);
        }
        return;
      }

      // options-finder conversational mode: show raw text response
      if (context === 'options-finder' && !forceSearch) {
        // Strip any accidental JSON fences from conversational response
        const cleanContent = rawContent?.replace(/```json[\s\S]*?```/gi, '')?.replace(/```[\s\S]*?```/g, '')?.trim() || "Could you tell me more about what you're looking for?";
        setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: cleanContent }]);
        return;
      }

      // product-details / category-selection: existing logic
      if (parsed) {
        const isReadyToComplete =
          (context === 'product-details' && parsed?.product_specifications) ||
          (context === 'category-selection' && parsed?.recommended_category);

        if (isReadyToComplete) {
          const summaryContent = context === 'product-details'
            ? `Based on your responses, here are the recommended specifications:\n\n${Object.entries(parsed?.product_specifications || {})?.map(([k, v]) => `• ${k?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}: ${v}`)?.join('\n')}\n\nConfidence: ${Math.round((parsed?.confidence || 0.8) * 100)}%`
            : `Based on your responses, I recommend:\n\n**Category**: ${parsed?.recommended_category}\n**Subcategory**: ${parsed?.recommended_subcategory}\n**Budget Estimate**: ${parsed?.budget_estimate}\n\nConfidence: ${Math.round((parsed?.confidence || 0.8) * 100)}%`;

          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: summaryContent,
            isAnalysis: true,
            analysisData: parsed
          };
          setMessages(prev => [...prev, assistantMessage]);
          setConversationState('complete');
        } else {
          const followUp = parsed?.next_steps?.[0] || "Could you tell me more about your specific needs?";
          setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: followUp }]);
        }
      } else {
        // Could not parse JSON at all — show raw content if available, else generic follow-up
        const fallback = rawContent?.trim() || "Could you tell me more about your specific needs?";
        setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: fallback }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I\'m having trouble connecting right now. Please try again in a moment."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Needs context: extract structured specs from free-form conversation ──────
  const extractNeedsSpecs = async (conversationHistory) => {
    const purchaseType = existingData?.basics?.purchaseType || 'general';
    const budget = existingData?.basics?.budgetRange || '';

    const productSpecSchemas = {
      'computers-laptops': `{"priceMax":"budget or max price","processor":"CPU model or spec","ram":"memory amount","storage":"storage size and type","graphics":"GPU model or spec","screenSize":"display size in inches","screenResolution":"display resolution","batteryLife":"battery duration","ports":"connectivity ports","weight":"device weight","operatingSystem":"windows|macos|linux|chrome","warranty":"warranty terms"}`,
      'tablets': `{"priceMax":"budget or max price","storage":"64GB|128GB|256GB|512GB|1TB","batteryLife":"battery duration","screenSize":"display size in inches","cameraSpecs":"camera details","processor":"chip model","carrierCompatibility":"unlocked|verizon|att|tmobile|wifi-only","waterproofRating":"IP68|IP67|IP65|not-rated","warranty":"warranty terms"}`,
      'smartphones': `{"priceMax":"budget or max price","storage":"128GB|256GB|512GB|1TB","screenSize":"display size in inches","batteryLife":"battery capacity or duration","processor":"chip model","carrierCompatibility":"unlocked|verizon|att|tmobile","waterproofRating":"IP68|IP67|IP65|not-rated","warranty":"warranty terms"}`,
      'car-purchase': `{"vehicleType":"sedan|suv|truck|coupe|hatchback|convertible","priceMax":"budget or max price","makeModelYear":"make model and year","mileage":"mileage if used or New","condition":"new|used|certified","fuelType":"gasoline|hybrid|electric|diesel","features":"additional features"}`,
      'home-purchase': `{"purchasePrice":"target purchase price","downPayment":"down payment amount or %","loanType":"conventional|fha|va|usda|jumbo","desiredLocation":"city or neighborhood","bedroomsBathrooms":"bed and bath count","squareFootage":"minimum sq ft","timeline":"when to close"}`,
      'furniture': `{"category":"sofa|bed|dining-table|desk|dresser|bookcase|other","material":"wood|metal|fabric|leather|glass|plastic|mixed","style":"modern|traditional|rustic|industrial|scandinavian","color":"preferred colors or finishes","size":"dimensions or size preferences","priceMax":"budget or max price","warranty":"warranty terms","leadTime":"delivery timeline"}`,
      'roofing': `{"projectType":"full-replacement|repair|inspection","roofingMaterial":"asphalt-shingles|metal|tile|slate|wood-shake","roofSize":"approximate sq ft","stories":"number of stories","priceMax":"max budget","timeline":"when needed"}`,
      'hvac': `{"serviceType":"installation|replacement|repair|maintenance","systemType":"central-ac|heat-pump|furnace|mini-split|boiler","homeSize":"sq ft","priceMax":"max budget","brandPreference":"brand if any","timeline":"when needed"}`,
      'electrical': `{"projectDescription":"describe the work","panelAmperage":"current or desired amperage","priceMax":"max budget","timeline":"when needed","permitsRequired":"yes|no|unknown"}`,
      'plumbing': `{"issueDescription":"describe the issue","location":"where in home","priceMax":"max budget","timeline":"how soon","homeAge":"year built if known"}`,
      'painting': `{"paintingType":"interior|exterior|both","areaSqFt":"total area","numberOfRooms":"number of rooms","colorPreferences":"color scheme","priceMax":"max budget","timeline":"when needed"}`,
      'flooring': `{"flooringMaterial":"hardwood|laminate|vinyl-plank|tile|carpet|bamboo","areaSqFt":"total area","numberOfRooms":"number of rooms","priceMax":"max budget","timeline":"when needed"}`,
    };

    const specSchema = productSpecSchemas?.[purchaseType] || `{"priceMax":"budget or max price","description":"what the user needs","style":"style preference","material":"material preference","size":"size or dimensions","timeline":"when needed"}`;

    const conversationText = conversationHistory
      ?.filter(m => m?.role !== 'system')
      ?.map(m => `${m?.role === 'user' ? 'User' : 'Assistant'}: ${m?.content}`)
      ?.join('\n');

    const extractionPrompt = `You are a data extraction assistant. Based on the conversation below about a ${purchaseType} purchase, extract the user's stated needs and fill in the product specifications JSON. Only include fields where the user has clearly stated a preference or requirement. Leave out fields where no information was given.

Product spec schema for ${purchaseType}:
${specSchema}

Conversation:
${conversationText}

Budget context: ${budget || 'not specified'}

Respond with ONLY valid JSON in this exact format (no explanation, no markdown):
{"product_specifications": <filled spec object using only keys from the schema above>, "confidence": <0-1 number>, "summary": "<one sentence summary of what the user needs>"}`;

    try {
      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          { role: 'system', content: extractionPrompt },
          { role: 'user', content: 'Extract the product specifications from the conversation above.' }
        ],
        { max_tokens: 600 }
      );
      const rawContent = result?.choices?.[0]?.message?.content || '';
      return extractJSON(rawContent);
    } catch (err) {
      console.error('Needs extraction error:', err);
      return null;
    }
  };

  // ── Needs context: conversational response ───────────────────────────────────
  const handleNeedsMessage = async (capturedMessage, currentResponses) => {
    const purchaseType = existingData?.basics?.purchaseType || 'item';
    const budget = existingData?.basics?.budgetRange || '';
    const budgetNote = budget ? ` Their budget is ${budget}.` : '';

    const conversationHistory = [
      ...messages
        ?.filter(m => m?.role === 'user' || m?.role === 'assistant')
        ?.map(m => ({ role: m?.role, content: m?.content })),
      { role: 'user', content: capturedMessage }
    ];

    let systemPrompt = `You are a friendly and knowledgeable shopping assistant helping a user define their needs for a ${purchaseType} purchase.${budgetNote} Your job is to have a natural conversation to understand exactly what they need — ask about type, style, material, size, color, features, budget, and timeline as relevant. Keep responses concise (2-4 sentences). Be warm and helpful. Do NOT return JSON. When you feel you have a good understanding of their needs (after 2-3 exchanges), end your response with a new line saying: "I think I have a good picture of your needs! Click 'Populate My Needs' below to fill in your product details automatically." Only say this once you genuinely have enough information.`;

    try {
      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          { role: 'system', content: systemPrompt },
          ...conversationHistory
        ],
        { max_tokens: 400 }
      );

      const rawContent = result?.choices?.[0]?.message?.content || '';
      // Strip any accidental JSON from conversational response
      const cleanContent = rawContent
        ?.replace(/```json[\s\S]*?```/gi, '')
        ?.replace(/```[\s\S]*?```/g, '')
        ?.replace(/\{[\s\S]*\}/g, '')
        ?.trim() || "Could you tell me more about what you're looking for?";

      // Detect if AI signaled it has enough info
      const hasEnoughInfo = cleanContent?.toLowerCase()?.includes("populate my needs") ||
        cleanContent?.toLowerCase()?.includes("good picture of your needs") ||
        currentResponses?.length >= 3;

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: cleanContent,
        isNeedsReady: hasEnoughInfo,
        conversationHistory: conversationHistory
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (hasEnoughInfo) {
        setConversationState('ready-to-populate');
      }
    } catch (err) {
      console.error('Needs chat error:', err);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Needs context: populate form with extracted specs ────────────────────────
  const handlePopulateNeeds = async (conversationHistory) => {
    setIsLoading(true);
    const extracted = await extractNeedsSpecs(conversationHistory || messages?.filter(m => m?.role === 'user' || m?.role === 'assistant')?.map(m => ({ role: m?.role, content: m?.content })));
    setIsLoading(false);

    if (extracted?.product_specifications && Object.keys(extracted?.product_specifications)?.length > 0) {
      onComplete({
        productSpecs: extracted?.product_specifications,
        aiAnalysis: extracted,
        summary: extracted?.summary || ''
      });
    } else {
      // Fallback: close and let user fill manually
      onComplete({ productSpecs: {}, aiAnalysis: null });
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || isLoading) return;

    const capturedMessage = inputMessage;

    const newUserMessage = {
      id: Date.now(),
      role: 'user',
      content: capturedMessage
    };

    setMessages(prev => [...prev, newUserMessage]);
    const currentResponses = [...userResponses, capturedMessage];
    setUserResponses(currentResponses);
    setInputMessage('');
    setIsLoading(true);

    // ── Needs context: use dedicated conversational handler ──────────────────
    if (context === 'needs') {
      await handleNeedsMessage(capturedMessage, currentResponses);
      return;
    }

    // Check for intelligent response patterns
    const intelligentCheck = generateIntelligentResponse(capturedMessage);

    if (intelligentCheck?.isIntelligent) {
      const intelligentMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: intelligentCheck?.response,
        isAnalysis: true,
        isIntelligentResponse: true,
        suggestedSpecs: intelligentCheck?.suggestedSpecs
      };
      setMessages(prev => [...prev, intelligentMessage]);
      setIsLoading(false);
      return;
    }

    // For options-finder: detect search intent to decide which prompt mode to use
    const searchTrigger = context === 'options-finder' && isSearchIntent(capturedMessage);
    await analyzeUserNeeds(currentResponses, searchTrigger, capturedMessage);
  };

  const handleComplete = (analysisData, suggestedSpecs = null) => {
    if (context === 'options-finder') {
      onComplete({
        suggestedOptions: analysisData?.suggested_options?.map(option => ({
          name: option?.name || 'AI Suggested Option',
          website: sanitizeWebsiteUrl(option?.website || ''),
          product_url: option?.product_url || '',
          price: option?.price_range || '',
          notes: option?.notes || option?.benefits || 'AI-generated option'
        })) || []
      });
    } else if (context === 'product-details') {
      onComplete({
        productSpecs: suggestedSpecs || analysisData?.product_specifications || {},
        aiAnalysis: analysisData
      });
    } else {
      onComplete(analysisData);
    }
  };

  const handleAddRecommendations = () => {
    const recommendationMessage = messages?.find(m => m?.showRecommendationButton);
    if (recommendationMessage) {
      onComplete({ suggestedOptions: [] });
    }
  };

  const handleAddSingleOption = (option, messageId, idx) => {
    onAddSingleOption?.({
      name: option?.name || 'AI Suggested Option',
      website: sanitizeWebsiteUrl(option?.website || ''),
      product_url: option?.product_url || '',
      price: option?.price_range || '',
      notes: option?.notes || option?.benefits || 'AI-generated option'
    });
    setAddedOptions(prev => ({ ...prev, [`${messageId}_${idx}`]: true }));
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFindOptionsForMe = async () => {
    if (isLoading || conversationState === 'complete') return;

    const triggerMessage = 'Find me the best options based on my needs';

    const newUserMessage = {
      id: Date.now(),
      role: 'user',
      content: triggerMessage
    };

    setMessages(prev => [...prev, newUserMessage]);
    const currentResponses = [...userResponses, triggerMessage];
    setUserResponses(currentResponses);
    setInputMessage('');
    setIsLoading(true);

    try {
      const purchaseType = existingData?.basics?.purchaseType || 'general';
      // Always use JSON-forcing prompt for the explicit "Find Options" button
      let systemPrompt = `You are an expert shopping advisor with real-time web search capabilities. The user is looking for a ${purchaseType} purchase. Based on their conversation and the following known needs/specs: ${JSON.stringify(existingData?.specs || {})}, search for and recommend 3-5 specific, current purchase options.\n        \n        Include:\n        - Specific product models with current pricing\n        - Best retailers (Amazon, Best Buy, manufacturer websites, specialty stores, etc.)\n        - Why each option fits their stated needs\n        - Price comparison across retailers\n        \n        Focus on generating 3-5 specific, actionable purchase options with realistic details including estimated price ranges and key benefits of each option.\n\nIMPORTANT URL RULES:\n- The "website" field MUST be the vendor root domain only (e.g. "livingspaces.com", "amazon.com", "bestbuy.com"). No display names, no parentheses.\n- The "product_url" field MUST be the DIRECT, SPECIFIC product page URL for that exact item (e.g. "https://www.livingspaces.com/products/specific-sofa-12345", "https://www.amazon.com/dp/B08XYZ123"). This must link directly to the product, NOT the homepage. If you cannot find the exact product URL, use the most specific category/search URL you can find.\n\nRespond with valid JSON only in this exact format: {"suggested_options": [{"name": string, "website": string, "product_url": string, "price_range": string, "benefits": string, "notes": string}], "confidence": number, "reasoning": string, "next_steps": [string]}`;

      const specsText = Object.entries(existingData?.specs || {})
        ?.filter(([, v]) => v && v !== '' && v !== false)
        ?.map(([k, v]) => `• ${k?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}: ${v}`)
        ?.join('\n');

      const userContent = `Find the best purchase options for a ${purchaseType} with the following requirements:\n${specsText || 'General requirements based on our conversation'}\nBudget: ${existingData?.basics?.budgetRange || 'not specified'}\nUser message: "${triggerMessage}"${currentResponses?.slice(0, -1)?.length > 0 ? `\nAdditional context: ${currentResponses?.slice(0, -1)?.join(' | ')}` : ''}`;

      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-reasoning-pro',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userContent }
        ],
        { max_tokens: 1500 }
      );

      const rawContent = result?.choices?.[0]?.message?.content || '';
      let parsed = extractJSON(rawContent);

      if (parsed?.suggested_options?.length > 0) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: `Here are the best options I found for your ${purchaseType}. Click "Add to Stack" on any option to save it, or use "Add All & Close" to add everything at once.`,
          isAnalysis: true,
          analysisData: parsed
        };
        setMessages(prev => [...prev, assistantMessage]);
        setConversationState('complete');
      } else {
        const fallbackContent = rawContent?.trim() || "I wasn't able to find specific options. Could you share more details about your budget or preferences?";
        setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: fallbackContent }]);
      }
    } catch (error) {
      console.error('Error finding options:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if we have enough needs to trigger a search
  const hasEnoughNeeds = context === 'options-finder' && (
    Object.entries(existingData?.specs || {})?.filter(([, v]) => v && v !== '' && v !== false)?.length > 0 ||
    userResponses?.length > 0
  );

  const findOptionsButtonVisible = context === 'options-finder' && hasEnoughNeeds && conversationState !== 'complete';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-2 border-white border-opacity-40">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">
                {context === 'options-finder' ? 'AI Options Finder' : context === 'needs' ? 'AI Needs Assistant' : context === 'product-details' ? 'AI Product Assistant' : 'AI Purchase Assistant'}
              </h3>
              <p className="text-xs text-white text-opacity-80" style={{color: 'rgba(255,255,255,0.85)'}}>
                {context === 'options-finder' ? 'Finding relevant purchase options for you' : context === 'needs' ? 'Understanding your needs to fill product details' : context === 'product-details' ? 'Helping you define product specifications' : 'Helping you find the right category'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-all"
          >
            &times;
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {messages?.map((message) => (
            <div
              key={message?.id}
              className={cn(
                "flex gap-3",
                message?.role === 'user' ? "flex-row-reverse" : "flex-row"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                message?.role === 'user'
                  ? "bg-primary text-white" :"bg-gradient-to-br from-orange-400 to-red-400 text-white"
              )}>
                {message?.role === 'user' ? (
                  <User className="w-4 h-4" />
                ) : (
                  <ShoppingBag className="w-4 h-4" />
                )}
              </div>

              <div className={cn(
                "max-w-[80%] rounded-2xl px-4 py-3",
                message?.role === 'user'
                  ? "bg-primary text-white rounded-tr-sm" :"bg-orange-50 text-gray-800 border border-orange-100 rounded-tl-sm"
              )}>
                <div className="text-sm whitespace-pre-line">
                  {message?.content}
                </div>

                {/* Needs context: Populate My Needs button */}
                {message?.isNeedsReady && context === 'needs' && (
                  <div className="mt-3 pt-3 border-t border-orange-200">
                    <button
                      onClick={() => handlePopulateNeeds(message?.conversationHistory)}
                      disabled={isLoading}
                      className="flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90 disabled:opacity-50"
                      style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
                    >
                      {isLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Extracting your needs...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4" /> Populate My Needs</>
                      )}
                    </button>
                  </div>
                )}

                {message?.showRecommendationButton && context === 'options-finder' && (
                  <div className="mt-3 pt-3 border-t border-orange-200">
                    <button
                      onClick={handleAddRecommendations}
                      className="flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
                      style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Add These Options
                    </button>
                  </div>
                )}

                {message?.isAnalysis && context === 'options-finder' && message?.analysisData?.suggested_options?.length > 0 ? (
                  <div className="mt-3 pt-3 border-t border-orange-200 space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recommended Options</p>
                    {message?.analysisData?.suggested_options?.map((option, idx) => (
                      <div key={idx} className="bg-white border border-orange-200 rounded-xl p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 leading-tight">{option?.name}</p>
                            {option?.price_range && (
                              <p className="text-xs text-orange-600 font-medium mt-0.5">{option?.price_range}</p>
                            )}
                          </div>
                        </div>
                        {option?.benefits && (
                          <p className="text-xs text-gray-600 leading-snug mb-2">{option?.benefits}</p>
                        )}
                        {option?.website && (
                          <a
                            href={option?.website?.startsWith('http') ? option?.website : `https://${option?.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline mb-2 block truncate"
                          >
                            🛒 {option?.website}
                          </a>
                        )}
                        <button
                          onClick={() => handleAddSingleOption(option, message?.id, idx)}
                          disabled={addedOptions?.[`${message?.id}_${idx}`]}
                          className={`w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-white text-xs font-semibold transition-all ${
                            addedOptions?.[`${message?.id}_${idx}`]
                              ? 'cursor-default' :'hover:opacity-90 active:scale-95'
                          }`}
                          style={{
                            background: addedOptions?.[`${message?.id}_${idx}`]
                              ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                              : 'linear-gradient(135deg, #F58A54, #E0528A)'
                          }}
                        >
                          {addedOptions?.[`${message?.id}_${idx}`] ? (
                            <><CheckCircle className="w-3.5 h-3.5" /> Added to Stack ✓</>
                          ) : (
                            <><Plus className="w-3.5 h-3.5" /> Add to Stack</>
                          )}
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleComplete(message?.analysisData, message?.suggestedSpecs)}
                        className="flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
                        style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Add All & Close
                      </button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setConversationState('gathering');
                          const continueMessage = {
                            id: Date.now(),
                            role: 'assistant',
                            content: context === 'product-details' ? "I understand you'd like to continue. What other product details can you share?" : "I understand you\'d like to continue. What other details can you share about your purchase?"
                          };
                          setMessages(prev => [...prev, continueMessage]);
                        }}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                ) : message?.isAnalysis ? (
                  <div className="mt-3 pt-3 border-t border-orange-200 flex gap-2">
                    <button
                      onClick={() => handleComplete(message?.analysisData, message?.suggestedSpecs)}
                      className="flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
                      style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {message?.isIntelligentResponse ? 'Yes, Populate Form' : context === 'product-details' ? 'Use These Specs' : 'Use This Category'}
                    </button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setConversationState('gathering');
                        const continueMessage = {
                          id: Date.now(),
                          role: 'assistant',
                          content: context === 'product-details' ? "I understand you'd like to continue. What other product details can you share?" : "I understand you\'d like to continue. What other details can you share about your purchase?"
                        };
                        setMessages(prev => [...prev, continueMessage]);
                      }}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      Continue
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-4 h-4 text-white" />
              </div>
              <div className="bg-orange-50 border border-orange-100 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                  <span className="text-sm text-gray-500">
                    {conversationState === 'analyzing' ? 'Analyzing your needs...' : 'Thinking...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          {findOptionsButtonVisible && (
            <div className="mb-3">
              <button
                onClick={handleFindOptionsForMe}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching for options...
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" />
                    Find Options for Me
                  </>
                )}
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder={context === 'options-finder' ? 'Tell me more about what you need...' : context === 'needs' ? 'Describe what you need in your own words...' : context === 'product-details' ? 'Describe your requirements...' : 'Tell me about your purchase...'}
              className="flex-1"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage?.trim() || isLoading}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #F58A54, #E0528A)'}}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            {context === 'options-finder' ?'Powered by Perplexity AI with real-time web search' : context === 'needs' ? 'AI-powered needs extraction — your details will be auto-filled' :'AI-powered assessment to help define your needs'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssessmentChat;